import React, { useState, useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// fix leaflet default icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
});

// component to auto fit map to route
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length > 1) {
      map.fitBounds(L.latLngBounds(points), { padding: [40, 40] });
    }
  }, [points, map]);
  return null;
}

export default function App() {
  const [currentPos, setCurrentPos] = useState(null);

  const [pickupText, setPickupText] = useState("");
  const [dropText, setDropText] = useState("");

  const [pickupSug, setPickupSug] = useState([]);
  const [dropSug, setDropSug] = useState([]);

  const [pickup, setPickup] = useState(null);
  const [drop, setDrop] = useState(null);

  const [route, setRoute] = useState([]);
  const [info, setInfo] = useState(null);

  // get current location
  useEffect(() => {
    navigator.geolocation?.getCurrentPosition((pos) => {
      setCurrentPos({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
      });
    });
  }, []);

  // debounce helper
  const debounce = (fn, delay) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), delay);
    };
  };

  // search India locations using Nominatim
  const searchPlace = async (text, setSug) => {
    if (text.length < 3) {
      setSug([]);
      return;
    }
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&countrycodes=in&limit=5&q=${encodeURIComponent(
          text
        )}`
      );
      const data = await res.json();
      setSug(data);
    } catch (e) {
      console.log("Search error", e);
      setSug([]);
    }
  };

  const debouncedPickupSearch = useMemo(
    () => debounce((t) => searchPlace(t, setPickupSug), 400),
    []
  );
  const debouncedDropSearch = useMemo(
    () => debounce((t) => searchPlace(t, setDropSug), 400),
    []
  );

  // when user clicks a suggestion
  const choosePlace = (place, type) => {
    const pos = { lat: +place.lat, lng: +place.lon };

    if (type === "pickup") {
      setPickup(pos);
      setPickupText(place.display_name);
      setPickupSug([]);
    } else {
      setDrop(pos);
      setDropText(place.display_name);
      setDropSug([]);
    }
  };

  // fetch driving route and distance
  useEffect(() => {
    if (!pickup || !drop) return;

    const getRoute = async () => {
      try {
        const url = `https://router.project-osrm.org/route/v1/driving/${pickup.lng},${pickup.lat};${drop.lng},${drop.lat}?overview=full&geometries=geojson`;

        const res = await fetch(url);
        const data = await res.json();

        if (!data.routes || data.routes.length === 0) {
          console.log("No route found");
          setRoute([]);
          setInfo(null);
          return;
        }

        const r = data.routes[0];

        // convert [lng,lat] -> [lat,lng] for leaflet
        const coords = r.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
        setRoute(coords);

        setInfo({
          distance: (r.distance / 1000).toFixed(2) + " km",
          time: Math.round(r.duration / 60) + " min",
        });
      } catch (e) {
        console.log("Routing error", e);
        setRoute([]);
        setInfo(null);
      }
    };

    getRoute();
  }, [pickup, drop]);

  const reset = () => {
    setPickup(null);
    setDrop(null);
    setPickupText("");
    setDropText("");
    setPickupSug([]);
    setDropSug([]);
    setRoute([]);
    setInfo(null);
  };

  const center = currentPos
    ? [currentPos.lat, currentPos.lng]
    : [20.5937, 78.9629];

  return (
    <div style={{ height: "100vh", width: "100%", position: "relative" }}>
      {/* top panel */}
      <div
        style={{
          position: "absolute",
          top: 10,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1000,
          background: "white",
          padding: 12,
          width: "95%",
          maxWidth: 520,
          borderRadius: 10,
          boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          fontFamily: "sans-serif",
        }}
      >
        <h3 style={{ textAlign: "center", margin: 0 }}>India Carpool Map</h3>

        <input
          placeholder="Search pickup"
          value={pickupText}
          onChange={(e) => {
            const v = e.target.value;
            setPickupText(v);
            debouncedPickupSearch(v);
          }}
          style={{ width: "100%", padding: 8, marginTop: 10 }}
        />
        {pickupSug.map((p, i) => (
          <div
            key={i}
            onClick={() => choosePlace(p, "pickup")}
            style={{ cursor: "pointer", padding: 6 }}
          >
            {p.display_name}
          </div>
        ))}

        <input
          placeholder="Search destination"
          value={dropText}
          onChange={(e) => {
            const v = e.target.value;
            setDropText(v);
            debouncedDropSearch(v);
          }}
          style={{ width: "100%", padding: 8, marginTop: 10 }}
        />
        {dropSug.map((p, i) => (
          <div
            key={i}
            onClick={() => choosePlace(p, "drop")}
            style={{ cursor: "pointer", padding: 6 }}
          >
            {p.display_name}
          </div>
        ))}

        {info && (
          <div style={{ marginTop: 10 }}>
            Distance: <b>{info.distance}</b> | Time: <b>{info.time}</b>
          </div>
        )}

        <button
          onClick={reset}
          style={{
            width: "100%",
            padding: 10,
            marginTop: 10,
            background: "#333",
            color: "white",
            border: "none",
            borderRadius: 6,
          }}
        >
          Reset
        </button>
      </div>

      {/* map */}
      <MapContainer
        center={center}
        zoom={currentPos ? 14 : 5}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          attribution="© OpenStreetMap contributors"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {currentPos && <Marker position={currentPos} />}
        {pickup && <Marker position={pickup} />}
        {drop && <Marker position={drop} />}

        {route.length > 0 && (
          <>
            <Polyline
              positions={route}
              pathOptions={{ color: "blue", weight: 5 }}
            />
            <FitBounds points={route} />
          </>
        )}
      </MapContainer>
    </div>
  );
}
